/**
 * Stops browser translation (e.g. Chrome "Translate to …") and DOM-rewriting
 * extensions from crashing React.
 *
 * Translation replaces React's text nodes with its own <font> nodes. When React
 * later removes or inserts next to one of its original nodes (e.g. the Pay button
 * switching to its loading state), the DOM call throws `NotFoundError` and the app
 * falls into the global error boundary mid-payment.
 *
 * When the node React targets is no longer where it expects, skip that single
 * DOM operation instead of throwing. Workaround from
 * https://github.com/facebook/react/issues/11538#issuecomment-417504600
 */
export const guardDomAgainstTranslation = () => {
  if (typeof Node !== 'function' || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;

  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.warn('Skipped removeChild: node was moved by a third party (e.g. translation).');

      return child;
    }

    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn('Skipped insertBefore: node was moved by a third party (e.g. translation).');

      return newNode;
    }

    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};
